/**
 * WSH — URL Param → Klaviyo Profile Property Writer: A2 acknowledgement notice.
 *
 * Consumer of `wsh:profile-property:acknowledged` dispatched by the writer.
 * Injects a dismissible notice into a reserved slot (collection pages), or
 * near the URL fragment anchor (e.g., #logic on /pages/daw-packs).
 *
 * Renders on BOTH the emit path AND the skip path — the copy is acknowledgement
 * ("Logic Pro it is") not write-completion, honest either way.
 *
 * No auto-fade. Dismissible only. Persists until the user closes it.
 *
 * Placement priority:
 *   1. document.getElementById('wsh-property-notice-slot') — reserved slot
 *      (rendered on every collection page via layout/theme.liquid)
 *   2. document.getElementById(raw_key) — anchor named by the URL param value
 *      (e.g., #logic on /pages/daw-packs) — inject IMMEDIATELY BEFORE the anchor
 *   3. Before <main> — last-resort insertion for pages that carry neither
 *      a slot nor a raw-key anchor (e.g. /pages/daw-packs with ?daw=other).
 *      Placing here keeps the notice below the sticky header in document
 *      order; prepending to <body> would put the notice ABOVE the header
 *      and displace the header downward on initial load.
 *
 * Copy branches on the param_name from the writer's event detail:
 *   - `daw`:
 *       * Session DAWs (logic/pro_tools/cubase/garageband/ableton/studio_one/reaper):
 *           "{property_value} it is. Everything from here arrives set up for {short}."
 *       * fl_studio:
 *           "Got it — FL Studio. We don't ship a dedicated FL Studio Sessions build
 *           yet, but every WSH kit opens in FL Studio via REX2 + WAV. Full list on
 *           this page."
 *       * other:
 *           "Got it. Every library opens in any DAW as WAV and REX. The DAW-ready
 *           sessions we've built so far are listed below."
 *   - `genre`:
 *       * "{Genre} it is. We'll lead with {Genre} from here."
 *       * jazz_blues writes property_value "Jazz" (Alia canonical) but the notice
 *         displays "Jazz & Blues" per brief.
 *   - `wsh_format` (URL param key is namespaced to avoid Shopify's reserved
 *                   `?format=` collection-route param; property write is still
 *                   "Select your format"):
 *       * full_band:  "Full Band it is. You'll see the full sessions first."
 *       * drums_only: "Drums Only it is. You'll see the drum libraries first."
 *   - `mood`:
 *       * "{Mood} it is. We'll lean your picks that way."
 *   - `wsh_missing` / `wsh_corner` / `wsh_last` / `wsh_set` / `wsh_creator`
 *     (PP-1a answer buttons):
 *       * "Got it. We've saved that to your sound."
 *       * wsh_creator=just_exploring: no notice (nothing is saved).
 *
 * Brief: reference/WSH_CC-8_URL_Param_Writer_Genre_Format_Brief.md
 *        reference/pp-1/WSH_CC_Brief_PP-1_S1_Answer_Buttons.md
 */
(function () {
  if (window.__wshProfilePropertyNoticeInit) return;
  window.__wshProfilePropertyNoticeInit = true;

  // Short-label overrides for the sessionful DAWs. Keys are the URL param raw
  // values (which match the wsh_daw metaobject `key` field).
  var SHORT_LABELS = {
    logic: 'Logic',
    pro_tools: 'Pro Tools',
    cubase: 'Cubase',
    garageband: 'GarageBand',
    ableton: 'Ableton',
    studio_one: 'Studio One',
    reaper: 'Reaper',
  };

  function copyForDaw(rawKey, propertyValue) {
    if (rawKey === 'fl_studio') {
      return "Got it — FL Studio. We don't ship a dedicated FL Studio Sessions build yet, but every WSH kit opens in FL Studio via REX2 + WAV. Full list on this page.";
    }
    if (rawKey === 'other') {
      return "Got it. Every library opens in any DAW as WAV and REX. The DAW-ready sessions we've built so far are listed below.";
    }
    var short = SHORT_LABELS[rawKey] || propertyValue;
    return propertyValue + ' it is. Everything from here arrives set up for ' + short + '.';
  }

  function copyForGenre(rawKey, propertyValue) {
    // jazz_blues writes property_value "Jazz" (Alia canonical) but the
    // customer-facing display label is "Jazz & Blues" per brief.
    var displayLabel = (rawKey === 'jazz_blues') ? 'Jazz & Blues' : propertyValue;
    return displayLabel + " it is. We'll lead with " + displayLabel + " from here.";
  }

  function copyForFormat(rawKey) {
    if (rawKey === 'full_band')  return "Full Band it is. You'll see the full sessions first.";
    if (rawKey === 'drums_only') return "Drums Only it is. You'll see the drum libraries first.";
    return null; // unreachable given writer allowlist
  }

  function copyForMood(propertyValue) {
    return propertyValue + " it is. We'll lean your picks that way.";
  }

  function resolveCopy(paramName, rawKey, propertyValue) {
    if (paramName === 'daw')        return copyForDaw(rawKey, propertyValue);
    if (paramName === 'genre')      return copyForGenre(rawKey, propertyValue);
    if (paramName === 'wsh_format') return copyForFormat(rawKey);
    if (paramName === 'mood')       return copyForMood(propertyValue);
    if (paramName === 'wsh_creator' && rawKey === 'just_exploring') return null; // nothing saved, no notice
    if (paramName === 'wsh_missing' || paramName === 'wsh_corner' || paramName === 'wsh_last'
        || paramName === 'wsh_set' || paramName === 'wsh_creator') return "Got it. We've saved that to your sound.";
    return null;
  }

  function findAnchor(rawKey) {
    var slot = document.getElementById('wsh-property-notice-slot');
    if (slot) return { node: slot, mode: 'slot' };
    var anchor = document.getElementById(rawKey);
    if (anchor) return { node: anchor, mode: 'before-anchor' };
    var main = document.querySelector('main#MainContent, main');
    if (main) return { node: main, mode: 'before-main' };
    return { node: document.body, mode: 'body-prepend' };
  }

  function build(text, rawKey) {
    var wrap = document.createElement('aside');
    wrap.className = 'wsh-property-notice';
    wrap.setAttribute('role', 'status');
    wrap.setAttribute('aria-live', 'polite');
    wrap.dataset.wshRawKey = rawKey;

    // Inner container — flex row, capped at --page-width, centered.
    var inner = document.createElement('div');
    inner.className = 'wsh-property-notice__inner';

    var content = document.createElement('div');
    content.className = 'wsh-property-notice__content';

    var eyebrow = document.createElement('div');
    eyebrow.className = 'wsh-property-notice__eyebrow';
    // Inline SVG check icon before the eyebrow label.
    eyebrow.innerHTML = '<svg class="wsh-property-notice__check" viewBox="0 0 12 12" aria-hidden="true" focusable="false"><path d="M1.5 6.5 L4.5 9.2 L10.5 2.8" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg><span>YOUR CHOICE</span>';

    var body = document.createElement('p');
    body.className = 'wsh-property-notice__body';
    body.textContent = text;

    content.appendChild(eyebrow);
    content.appendChild(body);

    var dismiss = document.createElement('button');
    dismiss.type = 'button';
    dismiss.className = 'wsh-property-notice__dismiss';
    dismiss.setAttribute('aria-label', 'Dismiss');
    dismiss.textContent = '×';
    dismiss.addEventListener('click', function () {
      wrap.remove();
    });

    inner.appendChild(content);
    inner.appendChild(dismiss);
    wrap.appendChild(inner);
    return wrap;
  }

  function place(node, target) {
    if (target.mode === 'slot') {
      // Replace slot contents (idempotent — clears prior render on repeat)
      target.node.innerHTML = '';
      target.node.appendChild(node);
    } else if (target.mode === 'before-anchor') {
      // Remove any prior notice for the same raw_key first (idempotent)
      var prior = document.querySelector('.wsh-property-notice[data-wsh-raw-key="' + node.dataset.wshRawKey + '"]');
      if (prior) prior.remove();
      target.node.parentNode.insertBefore(node, target.node);
    } else if (target.mode === 'before-main') {
      var priorMain = document.querySelector('.wsh-property-notice[data-wsh-raw-key="' + node.dataset.wshRawKey + '"]');
      if (priorMain) priorMain.remove();
      target.node.parentNode.insertBefore(node, target.node);
    } else {
      // Body prepend (last resort — should be unreachable in practice)
      var priorBody = document.querySelector('body > .wsh-property-notice[data-wsh-raw-key="' + node.dataset.wshRawKey + '"]');
      if (priorBody) priorBody.remove();
      target.node.insertBefore(node, target.node.firstChild);
    }
  }

  document.addEventListener('wsh:profile-property:acknowledged', function (e) {
    try {
      var d = (e && e.detail) || {};
      var paramName = d.param_name;
      var rawKey = d.raw_key;
      var propertyValue = d.property_value;
      if (!paramName || !rawKey || !propertyValue) return;
      var text = resolveCopy(paramName, rawKey, propertyValue);
      if (!text) return; // unknown paramName — silent no-op
      var target = findAnchor(rawKey);
      var notice = build(text, rawKey);
      place(notice, target);
    } catch (_) { /* non-fatal */ }
  });
})();
